import i18n from '../i18n'
import { ENV_URL } from '../app/constants'

const SITE_NAME = 'Sitio de Tiendas'
const DEFAULT_LOGO_PATH = '/logosdt256.png'
const ORGANIZATION_LOGO_PATH = '/logosdttext.png'

function getOrganizationId() {
    return `${getBaseUrl()}/#organization`
}

function getWebsiteId() {
    return `${getBaseUrl()}/#website`
}

function getWebPageId(url) {
    return `${url || getCurrentUrl()}#webpage`
}

function getOrganizationEntity() {
    return {
        '@id': getOrganizationId(),
        '@type': 'Organization',
        name: SITE_NAME,
        url: getBaseUrl(),
        logo: toAbsoluteUrl(ORGANIZATION_LOGO_PATH),
    }
}

function getContactPoint(language) {
    return {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: '+59897228603',
        email: language === 'pt'
            ? 'contato@seuproximonegocio.com.br'
            : 'info@sitiodetiendas.com',
        areaServed: [
            'AR',
            'BR',
            'CL',
            'CO',
            'MX',
            'PE',
            'UY',
        ],
        availableLanguage: [
            language === 'pt' ? 'Portuguese' : 'Spanish',
        ],
    }
}

function getAboutPageMembers() {
    return [
        {
            '@type': 'Person',
            name: 'Andrea Bertone',
            jobTitle: i18n.t('about-us.members.role1'),
        },
        {
            '@type': 'Person',
            name: 'Celina Durrieu',
            jobTitle: i18n.t('about-us.members.role3'),
        },
        {
            '@type': 'Person',
            name: 'Maximiliano Sádaba',
            jobTitle: i18n.t('about-us.members.role2'),
        },
        {
            '@type': 'Person',
            name: 'Candela Vitale',
            jobTitle: i18n.t('about-us.members.role6'),
        },
        {
            '@type': 'Person',
            name: 'Mariana Hortal Sueldo',
            jobTitle: i18n.t('about-us.members.role4'),
        },
        {
            '@type': 'Person',
            name: 'Gabriela Sorrentino',
            jobTitle: i18n.t('about-us.members.role5'),
        },
        {
            '@type': 'Person',
            name: 'Miguel Domenech',
            jobTitle: i18n.t('about-us.members.role7'),
        },
    ]
}

function getBaseUrl() {
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin
    }

    return 'https://sitiodetiendas.com'
}

function toAbsoluteUrl(path) {
    if (!path) {
        return null
    }

    if (/^https?:\/\//i.test(path)) {
        return path
    }

    if (path.startsWith('/')) {
        return `${getBaseUrl()}${path}`
    }

    return `${getBaseUrl()}/${path}`
}

function getCurrentUrl() {
    if (typeof window !== 'undefined' && window.location) {
        return `${window.location.origin}${window.location.pathname}`
    }

    return getBaseUrl()
}

function cleanText(value) {
    if (!value) {
        return ''
    }

    if (typeof document === 'undefined') {
        return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    }

    const div = document.createElement('div')
    div.innerHTML = value

    return (div.textContent || div.innerText || '')
        .replace(/\s+/g, ' ')
        .trim()
}

function formatCurrencyValue(currency, value) {
    if (value === null || value === undefined || value === '') {
        return null
    }

    return `${currency ? `${currency} ` : ''}${value}`
}

function formatBusinessAge(value, language) {
    if (!Number.isFinite(value) || value < 0) {
        return null
    }

    const singular = language === 'pt' ? 'ano' : 'año'
    const plural = language === 'pt' ? 'anos' : 'años'

    return `${value} ${value === 1 ? singular : plural}`
}

function formatRoiValue(roi, language) {
    if (!Number.isFinite(roi) || roi < 0) {
        return null
    }

    const singularYear = language === 'pt' ? 'ano' : 'año'
    const pluralYear = language === 'pt' ? 'anos' : 'años'
    const singularMonth = language === 'pt' ? 'mês' : 'mes'
    const pluralMonth = language === 'pt' ? 'meses' : 'meses'

    let years = Math.floor(roi)
    let months = Math.round((roi - years) * 12)

    if (months === 12) {
        years += 1
        months = 0
    }

    if (years > 0 && months > 0) {
        return `${years} ${years === 1 ? singularYear : pluralYear} ${months} ${months === 1 ? singularMonth : pluralMonth}`
    }

    if (years > 0) {
        return `${years} ${years === 1 ? singularYear : pluralYear}`
    }

    return `${months} ${months === 1 ? singularMonth : pluralMonth}`
}

function formatPercentageValue(value) {
    if (!Number.isFinite(value)) {
        return null
    }

    return `${value}%`
}

export function buildOrganizationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        ...getOrganizationEntity(),
    }
}

export function buildWebsiteSchema() {
    return {
        '@context': 'https://schema.org',
        '@id': getWebsiteId(),
        '@type': 'WebSite',
        name: SITE_NAME,
        url: getBaseUrl(),
        publisher: {
            '@id': getOrganizationId(),
        },
        inLanguage: i18n.language || 'es',
    }
}

export function buildWebPageSchema({ name, description, url, type = 'WebPage' }) {
    const pageUrl = url || getCurrentUrl()

    return {
        '@context': 'https://schema.org',
        '@id': getWebPageId(pageUrl),
        '@type': type,
        name: cleanText(name),
        description: cleanText(description),
        url: pageUrl,
        isPartOf: {
            '@id': getWebsiteId(),
        },
        publisher: {
            '@id': getOrganizationId(),
        },
        inLanguage: i18n.language || 'es',
    }
}

export function buildBreadcrumbSchema(items) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
        })),
    }
}

export function buildBlogSchema(blogDetail) {
    if (!blogDetail) {
        return null
    }

    const pageUrl = getCurrentUrl()
    const articleImage = blogDetail.preview_image
        ? `${ENV_URL}${blogDetail.storage_path}${blogDetail.preview_image}`
        : null

    return {
        '@context': 'https://schema.org',
        '@id': `${pageUrl}#${blogDetail.type === 'prensa' ? 'news-article' : 'blog-post'}`,
        '@type': blogDetail.type === 'prensa' ? 'NewsArticle' : 'BlogPosting',
        headline: blogDetail.title,
        description: cleanText(blogDetail.subtitle),
        datePublished: blogDetail.date,
        dateModified: blogDetail.updated_at,
        mainEntityOfPage: {
            '@id': getWebPageId(pageUrl),
        },
        isPartOf: {
            '@id': getWebsiteId(),
        },
        url: pageUrl,
        image: articleImage || undefined,
        inLanguage: blogDetail.language || i18n.language || 'es',
        author: {
            '@id': getOrganizationId(),
        },
        publisher: {
            '@id': getOrganizationId(),
        },
    }
}

export function buildBusinessSchema(business, language) {
    if (!business) {
        return null
    }

    const pageUrl = getCurrentUrl()
    const currency = business?.user?.country?.currency_iso_code || ''
    const countryName = language === 'pt'
        ? business?.user?.country?.name_pt
        : business?.user?.country?.name_es

    const businessType = language === 'pt'
        ? business?.business_type?.name_pt
        : business?.business_type?.name_es

    const businessCategory = language === 'pt'
        ? business?.business_category?.name_pt
        : business?.business_category?.name_es

    const labels = language === 'pt'
        ? {
            businessType: 'Tipo de negócio',
            category: 'Categoria',
            country: 'País',
            age: 'Antiguidade do negócio',
            annualProfit: 'Lucro anual',
            annualRevenue: 'Faturamento bruto anual',
            roi: 'Recuperação do investimento',
            followers: 'Seguidores',
            subscribers: 'Inscritos',
            organicVisits: 'Visitas orgânicas mensais',
            hasDomain: 'Inclui domínio',
            hasWebsite: 'Inclui site',
            hasBrand: 'Inclui marca registrada',
            hasApp: 'Inclui app',
            yes: 'Sim',
        }
        : {
            businessType: 'Tipo de negocio',
            category: 'Categoría',
            country: 'País',
            age: 'Antigüedad del negocio',
            annualProfit: 'Ganancia anual',
            annualRevenue: 'Facturación bruta anual',
            roi: 'Recuperación de inversión',
            followers: 'Seguidores',
            subscribers: 'Suscriptores',
            organicVisits: 'Visitas orgánicas mensuales',
            hasDomain: 'Incluye dominio',
            hasWebsite: 'Incluye web',
            hasBrand: 'Incluye marca registrada',
            hasApp: 'Incluye app',
            yes: 'Sí',
        }

    const additionalProperty = [
        {
            '@type': 'PropertyValue',
            name: labels.businessType,
            value: businessType,
        },
        {
            '@type': 'PropertyValue',
            name: labels.category,
            value: businessCategory,
        },
        {
            '@type': 'PropertyValue',
            name: labels.country,
            value: countryName,
        },
        {
            '@type': 'PropertyValue',
            name: labels.age,
            value: formatBusinessAge(business.business_age_stat, language),
        },
        {
            '@type': 'PropertyValue',
            name: labels.annualProfit,
            value: formatCurrencyValue(currency, business.average_gross_profit_stat),
        },
        {
            '@type': 'PropertyValue',
            name: labels.annualRevenue,
            value: formatCurrencyValue(currency, business.average_gross_revenue_stat),
        },
        {
            '@type': 'PropertyValue',
            name: labels.roi,
            value: formatRoiValue(business.roi, language),
        },
        {
            '@type': 'PropertyValue',
            name: labels.followers,
            value: business.total_social_network_followers_stat,
        },
        {
            '@type': 'PropertyValue',
            name: labels.subscribers,
            value: business.total_email_subscribers_stat,
        },
        {
            '@type': 'PropertyValue',
            name: labels.organicVisits,
            value: formatPercentageValue(business.total_monthly_organic_visits_stat),
        },
        business.is_domain_stat === 1 ? {
            '@type': 'PropertyValue',
            name: labels.hasDomain,
            value: labels.yes,
        } : null,
        business.is_website_stat === 1 ? {
            '@type': 'PropertyValue',
            name: labels.hasWebsite,
            value: labels.yes,
        } : null,
        business.is_registered_brand_stat === 1 ? {
            '@type': 'PropertyValue',
            name: labels.hasBrand,
            value: labels.yes,
        } : null,
        business.is_app_stat === 1 ? {
            '@type': 'PropertyValue',
            name: labels.hasApp,
            value: labels.yes,
        } : null,
    ].filter(Boolean)

    return {
        '@context': 'https://schema.org',
        '@id': `${pageUrl}#product`,
        '@type': 'Product',
        name: business.title || `${businessType} de ${businessCategory}`,
        description: cleanText(business.description),
        category: businessCategory,
        url: pageUrl,
        mainEntityOfPage: {
            '@id': getWebPageId(pageUrl),
        },
        offers: {
            '@type': 'Offer',
            price: business.algorithm_local_price,
            priceCurrency: currency,
            url: pageUrl,
            availability: 'https://schema.org/InStock',
        },
        additionalProperty,
    }
}

export function buildFaqSchema() {
    const pricingUrl = `${getBaseUrl()}/precios`

    const sellerQuestions = Array.from({ length: 11 }, (_, index) => ({
        question: i18n.t(`faqs.vendedor.question${index + 1}`),
        answer: i18n.t(`faqs.vendedor.answer${index + 1}`, { link: pricingUrl }),
    }))

    const buyerQuestions = Array.from({ length: 13 }, (_, index) => ({
        question: i18n.t(`faqs.comprador.question${index + 1}`),
        answer: i18n.t(`faqs.comprador.answer${index + 1}`, { link: pricingUrl }),
    }))

    const questions = [...sellerQuestions, ...buyerQuestions]

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions.map((item) => ({
            '@type': 'Question',
            name: cleanText(item.question),
            acceptedAnswer: {
                '@type': 'Answer',
                text: cleanText(item.answer),
            },
        })),
    }
}

export function buildHomeSchemas() {
    return [
        buildOrganizationSchema(),
        buildWebsiteSchema(),
        buildWebPageSchema({
            name: i18n.t("section-titles.home"),
            description: i18n.t("section-titles.home_desc"),
            url: getCurrentUrl(),
        }),
    ]
}

export function buildCollectionPageSchema({ name, description, url }) {
    return buildWebPageSchema({
        name,
        description,
        url,
        type: 'CollectionPage',
    })
}

export function buildAboutPageSchema({ name, description, url }) {
    const organizationDescription = i18n.t('homepage.who-we-are.text')

    return {
        ...buildWebPageSchema({
            name,
            description,
            url,
            type: 'AboutPage',
        }),
        about: {
            '@id': getOrganizationId(),
        },
        mainEntity: {
            ...getOrganizationEntity(),
            description: cleanText(organizationDescription),
            member: getAboutPageMembers(),
        },
    }
}

export function buildContactPageSchema({ name, description, url, language }) {
    return {
        ...buildWebPageSchema({
            name,
            description,
            url,
            type: 'ContactPage',
        }),
        about: {
            '@id': getOrganizationId(),
        },
        contactPoint: [
            getContactPoint(language || i18n.language || 'es'),
        ],
    }
}

export function buildBlogCollectionPageSchema({ name, description, url, type }) {
    const pageDescription = type === 'prensa'
        ? i18n.t('section-titles.newsSection_desc')
        : i18n.t('section-titles.blogSection_desc')

    return buildCollectionPageSchema({
        name,
        description: description || pageDescription,
        url,
    })
}

export function buildHowToSchema({ name, description, steps }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: cleanText(name),
        description: cleanText(description),
        step: steps.map((step, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: cleanText(step.name),
            text: cleanText(step.text),
        })),
    }
}

export function buildHowToBuySchema() {
    const title = i18n.t('homepage.how-to-buy.title')

    return buildHowToSchema({
        name: title,
        description: i18n.t('homepage.how-to-buy.text'),
        steps: [
            {
                name: `${title} - Paso 1`,
                text: i18n.t('homepage.how-to-buy.cards.1'),
            },
            {
                name: `${title} - Paso 2`,
                text: i18n.t('homepage.how-to-buy.cards.2'),
            },
            {
                name: `${title} - Paso 3`,
                text: i18n.t('homepage.how-to-buy.cards.3'),
            },
        ],
    })
}

export function buildHowToSellSchema() {
    const title = i18n.t('homepage.how-to-sell.title')

    return buildHowToSchema({
        name: title,
        description: i18n.t('homepage.how-to-sell.text'),
        steps: [
            {
                name: `${title} - Paso 1`,
                text: i18n.t('homepage.how-to-sell.cards.1'),
            },
            {
                name: `${title} - Paso 2`,
                text: i18n.t('homepage.how-to-sell.cards.2'),
            },
            {
                name: `${title} - Paso 3`,
                text: i18n.t('homepage.how-to-sell.cards.3'),
            },
        ],
    })
}
